import React from 'react';
import { ChevronRight } from 'lucide-react';
import { AmbianceBand } from '../Ambiance/AmbianceBand';

/**
 * Les briques visuelles du tableau de bord, reprises telles quelles des lots 4, 5 et 6.
 *
 * L'écran d'accueil était resté sur l'ancien vocabulaire : cartes `--bg-card` à grands rayons,
 * titre en 3xl, ombres portées, apparition des tuiles en cascade, et une couleur écrite en dur par
 * tuile (vert, violet, orange, bleu) — ce que la règle III d'AI_DEVELOPER_RULES.md interdit
 * explicitement. Les modules déjà refondus utilisent un seul jeu : `bg-surface`, `border-border`,
 * rayons 2xl pour l'en-tête et xl pour les sections, sur-titres en capitales de 10 px, pilules de
 * 34 px, et RIEN d'animé sous la bande d'ambiance — c'est le seul endroit qui bouge.
 *
 * Habillage seulement (décision d'Emeni du 16/09) : mêmes chiffres, mêmes blocs, mêmes raccourcis,
 * aux mêmes conditions d'affichage. Rien n'a été ajouté ni retiré du contenu.
 */

export const SUR_TITRE = 'text-[10px] font-bold uppercase tracking-[.09em] text-content-muted';
export const PILL = 'h-[34px] px-[13px] inline-flex items-center gap-[7px] rounded-full border border-border bg-surface text-[12.5px] font-semibold text-content-muted whitespace-nowrap shrink-0 hover:border-content hover:text-content transition-colors';

interface EnTeteProps {
    /** Nom de l'établissement, en surtitre dans la bande — vide pour le super admin. */
    societe?: string;
    titre: string;
    sousTitre: string;
    /** Pilules d'action à droite de la barre de contrôles. */
    actions?: React.ReactNode;
}

/** En-tête commun à tous les modules refondus : bande d'ambiance de 96 px, puis la barre. */
export const DashboardHeader: React.FC<EnTeteProps> = ({ societe, titre, sousTitre, actions }) => (
    <header className="bg-surface border border-border rounded-2xl overflow-hidden print-hidden">
        <AmbianceBand className="h-[96px]">
            <div className="h-[96px] px-[18px] py-[15px] flex flex-col justify-between">
                <div className="flex items-center justify-between gap-3">
                    <span className="text-[10.5px] font-bold uppercase tracking-[.08em] text-content-muted truncate">{societe || ''}</span>
                </div>
                <div className="text-[21px] font-bold tracking-[-0.02em] text-content leading-none">{titre}</div>
            </div>
        </AmbianceBand>
        <div className="px-[18px] py-3.5 border-t border-border flex flex-wrap items-center justify-between gap-4">
            <p className="text-[11.5px] text-content-muted min-w-0">{sousTitre}</p>
            {actions}
        </div>
    </header>
);

export interface Chiffre {
    label: string;
    value: string;
    icon: React.ComponentType<{ size?: number; className?: string }>;
}

/**
 * Les quatre chiffres de l'établissement.
 *
 * Une seule teinte pour les quatre, celle de la marque, au lieu des quatre couleurs fixes d'avant :
 * ces couleurs ne voulaient rien dire — elles ne désignaient ni un état ni une urgence, juste un
 * ordre d'affichage. Les quatre couleurs de la charte sont réservées aux états (succès, attente,
 * alerte, danger) ; les dépenser pour décorer des compteurs les rendrait illisibles là où elles
 * portent un sens.
 */
export const DashboardStats: React.FC<{ chiffres: Chiffre[]; chargement?: boolean }> = ({ chiffres, chargement }) => {
    if (chargement && chiffres.length === 0) {
        return (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="bg-surface border border-border rounded-xl p-5 h-[104px] animate-pulse" />
                ))}
            </div>
        );
    }
    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {chiffres.map((c) => (
                <div key={c.label} className="bg-surface border border-border rounded-xl p-5">
                    <div className="flex items-start justify-between gap-2">
                        <span className={SUR_TITRE}>{c.label}</span>
                        <c.icon size={15} className="text-content-muted shrink-0" />
                    </div>
                    <p className="text-[28px] font-bold tracking-[-0.02em] text-content leading-none mt-3">{c.value}</p>
                </div>
            ))}
        </div>
    );
};

interface RaccourciProps {
    icon: React.ComponentType<{ size?: number; className?: string }>;
    titre: string;
    description: string;
    onClick: () => void;
    /** Le chevron ne s'affiche que si le raccourci mène ailleurs ; une fenêtre n'en a pas. */
    mèneAilleurs?: boolean;
}

/** Une tuile cliquable : ouvrir une fenêtre, ou aller sur un autre écran. */
export const DashboardShortcut: React.FC<RaccourciProps> = ({ icon: Icon, titre, description, onClick, mèneAilleurs }) => (
    <button
        type="button"
        onClick={onClick}
        className="w-full flex items-center justify-between gap-4 bg-surface border border-border rounded-xl p-5 text-left hover:border-content transition-colors"
    >
        <div className="flex items-center gap-3.5 min-w-0">
            <div className="w-10 h-10 rounded-[10px] bg-surface-soft border border-border flex items-center justify-center shrink-0">
                <Icon size={17} className="text-content-muted" />
            </div>
            <div className="min-w-0">
                <p className="text-[13px] font-bold text-content">{titre}</p>
                <p className="text-[11.5px] text-content-muted mt-0.5">{description}</p>
            </div>
        </div>
        {mèneAilleurs && <ChevronRight size={17} className="text-content-muted shrink-0" />}
    </button>
);

/** Section neutre à la charte : cadre, rayon xl, sur-titre en capitales. */
export const DashboardSection: React.FC<{ titre?: string; className?: string; children: React.ReactNode }> = ({ titre, className = '', children }) => (
    <section className={`bg-surface border border-border rounded-xl p-5 ${className}`}>
        {titre && <div className={`${SUR_TITRE} mb-3`}>{titre}</div>}
        {children}
    </section>
);
